import type { Dispatch } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import { uniq, flatten, concat, compact } from 'lodash';
import { AdminStateSlice, UserData } from '../../types';
import { BASIC_ADMIN_PERMISSIONS } from 'common/constants';
import TCClient from 'client';
import { actions, store, thunk } from 'redux-stores';

const CONSUMER_ORG = 'UpM28mnfW65vJkbqEATtKvxm';

export const initiateAdminSession = async (
  dispatch: Dispatch,
  initialOrgId: string,
  switchApp: (app: string) => void,
  getAdminRolesInOrganization: (orgId: string) => { basicAdminPermissions: string[] }
) => {
  try {
    await loadOrganization(dispatch, initialOrgId, {
      initialLoad: true,
      getAdminRolesInOrganization,
    });
  } catch (e) {
    switchApp('Messages');
    console.error('Error when trying to initiate admin session');
  }
};

export const switchOrganization = async (
  dispatch: Dispatch,
  selectedOrganizationId: string,
  getAdminRolesInOrganization: (orgId: string) => { basicAdminPermissions: string[] }
) => {
  try {
    await loadOrganization(dispatch, selectedOrganizationId, {
      getAdminRolesInOrganization,
    });
  } catch (e) {
    console.error('Error when trying to fetch account settings');
  }
};

export const setToastSettings = async (
  toastSettings: Partial<AdminStateSlice['toastSettings']>
) => {
  store.dispatch(
    actions.setToastSettings({
      ...toastSettings,
      onClose: () => {
        toastSettings.onClose?.();
        setToastSettings({ open: false });
      },
    })
  );
};

export const fetchUserPermissions = async (userId: string) => {
  const {
    admin: { selectedOrganization },
  } = store.getState();
  const permissions = await TCClient.adminUsers.getUserPermissions({
    orgId: selectedOrganization.token,
    userId: userId,
  });
  return permissions;
};

export const loadOrganizationPermissions = async (
  dispatch: Dispatch,
  selectedOrganizationId: string
) => {
  const settings = await TCClient.adminUsers.getAccountSettings({});
  const selectedOrg = settings?.permissions?.organization?.find(
    (org) => org.organizationId === selectedOrganizationId
  );
  const permissions = compact(
    uniq(flatten(concat([selectedOrg?.permission], [settings?.permissions?.product?.permission])))
  );
  dispatch(actions.setPermissions({ permissions }));
};

const getIsSettingsAdmin = (permissions: string[]) => {
  return permissions.some((permission) => permission === 'admin');
};

const getHasBasicAdminAccess = (permissions: string[]) => {
  const permission = permissions.find(
    (permission) => BASIC_ADMIN_PERMISSIONS[permission as keyof typeof BASIC_ADMIN_PERMISSIONS]
  );
  return permission !== undefined;
};

const loadOrganization = async (
  dispatch: Dispatch,
  selectedOrganizationId: string,
  options?: {
    initialLoad?: boolean;
    getAdminRolesInOrganization: (orgId: string) => { basicAdminPermissions: string[] };
  }
) => {
  const settings = await TCClient.adminUsers.getAccountSettings({});
  if (options?.initialLoad) {
    const {
      admin: { organizations },
    } = store.getState();
    if (!organizations[selectedOrganizationId]) {
      const orgs = settings?.membership?.organization
        .filter(({ admin, token }: { admin: boolean; token: string }) => {
          const { basicAdminPermissions } = options?.getAdminRolesInOrganization(token);
          return (
            (token !== CONSUMER_ORG && admin) ||
            (token !== CONSUMER_ORG && getHasBasicAdminAccess(basicAdminPermissions || []))
          );
        })
        .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
      dispatch(actions.setOrganizations({ organizations: orgs }));
    }
  }
  const selectedOrg = settings?.permissions?.organization?.find(
    (org) => org.organizationId === selectedOrganizationId
  );

  const permissions = compact(
    uniq(flatten(concat([selectedOrg?.permission], [settings?.permissions?.product?.permission])))
  );
  const { basicAdminPermissions = [] } =
    options?.getAdminRolesInOrganization(selectedOrganizationId) || {};
  const isSettingsAdmin = getIsSettingsAdmin(permissions);
  const hasBasicAdminAccess = getHasBasicAdminAccess(basicAdminPermissions);
  if (!isSettingsAdmin && !hasBasicAdminAccess) {
    throw new Error('User does not have admin privileges');
  }
  batch(() => {
    dispatch(actions.setPermissions({ permissions }));
    dispatch(actions.setSelectedOrganizationId({ selectedOrganizationId }));
    dispatch(
      actions.setSelectedPage({
        selectedPage: !isSettingsAdmin && hasBasicAdminAccess ? 'Org Settings' : 'Users',
      })
    );
    dispatch(actions.setAdminIsInitiated());
    dispatch(actions.customerSupportSearchOrganizationData([]));
    dispatch(actions.customerSupportSearchAccountData(null));
    dispatch(actions.customerSupportSearchParseAccountData({} as UserData));
    dispatch(actions.customerSupportSearchSearchInput(''));
  });

  await thunk.fetchSettings(dispatch);
};
